import React from 'react';

import { cc } from './icons';
import { useOverlay } from './overlays';

// ─────────────────────────────────────────────────────────────────────────────
// Gramática visual de la consola. Cada pieza es la traducción literal de una
// función del prototipo: `read`/`readL`, `chips`, `wf`, la barra de composición
// y la fila. Nada de esto decide nada de negocio — son formas.
// ─────────────────────────────────────────────────────────────────────────────

/** Rótulo en versalitas con tracking. `right` va pegado a la derecha. */
export function Eyebrow({
  children,
  right,
  info,
  head,
  style,
}: {
  children: React.ReactNode;
  right?: React.ReactNode;
  info?: React.ReactNode;
  head?: boolean;
  style?: React.CSSProperties;
}) {
  return (
    <p
      className={`eyebrow${head ? ' ehead' : ''}`}
      style={right && !head ? { justifyContent: 'space-between', ...style } : style}
    >
      <span>
        {children}
        {info ? <Info>{info}</Info> : null}
      </span>
      {right}
    </p>
  );
}

/** Ícono de ayuda con su globo (el `.inf` del prototipo). */
export function Info({ children }: { children: React.ReactNode }) {
  return (
    <span className="inf">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 11v5.5M12 7.6v.1" />
      </svg>
      <span className="tip">{children}</span>
    </span>
  );
}

/** Lectura alineada a la izquierda: clave, cifra grande y pie. */
export function ReadL({
  k,
  v,
  s,
  cls,
  info,
}: {
  k: React.ReactNode;
  v: React.ReactNode;
  s?: React.ReactNode;
  cls?: string;
  info?: React.ReactNode;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      <span
        style={{
          fontSize: '.56rem',
          textTransform: 'uppercase',
          letterSpacing: '.16em',
          color: 'var(--faint)',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
        }}
      >
        {k}
        {info ? <Info>{info}</Info> : null}
      </span>
      <span className={`mid ${cls || ''}`} style={{ marginTop: 10 }}>
        {v}
      </span>
      {s ? (
        <span className="sml" style={{ marginTop: 9 }}>
          {s}
        </span>
      ) : null}
    </div>
  );
}

/** Lectura alineada a la derecha (la del visor de Hoy). */
export function Read({
  k,
  v,
  s,
  cls,
  info,
}: {
  k: React.ReactNode;
  v: React.ReactNode;
  s?: React.ReactNode;
  cls?: string;
  info?: React.ReactNode;
}) {
  return (
    <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      <span
        style={{
          fontSize: '.56rem',
          textTransform: 'uppercase',
          letterSpacing: '.16em',
          color: 'var(--faint)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 6,
        }}
      >
        {k}
        {info ? <Info>{info}</Info> : null}
      </span>
      <span className={`mid ${cls || ''}`} style={{ marginTop: 9 }}>
        {v}
      </span>
      {s ? (
        <span className="sml" style={{ marginTop: 8 }}>
          {s}
        </span>
      ) : null}
    </div>
  );
}

export const Spine = () => <div className="spine" />;
export const Rule = ({ className }: { className?: string }) => (
  <hr className={`rule${className ? ' ' + className : ''}`} />
);
export const Sent = () => <div className="sent" />;

/** Fila de lecturas con su separación, que colapsa sola.
 *
 *  Reemplaza al patrón `<div className="g g3"><ReadL/><Spine/><ReadL/>…</div>`:
 *  la espina deja de ser un nodo y pasa a ser el borde izquierdo de cada
 *  lectura, así el colapso en pantallas angostas se resuelve en la hoja (ver
 *  `.reads` en console.css) en vez de dejar espinas sueltas a mitad de fila.
 *  Admite de dos a seis columnas. */
export function Reads({
  children,
  className,
  style,
}: {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}) {
  const n = Math.min(6, Math.max(2, React.Children.toArray(children).filter(Boolean).length));
  return (
    <div className={`reads n${n}${className ? ' ' + className : ''}`} style={style}>
      {children}
    </div>
  );
}

/** Punto de color con su nombre: cuando un gráfico dibuja dos categorías, el
 *  color es el dato y hay que decir cuál es cuál. */
export function Lgd({
  c,
  children,
  v,
}: {
  c: string;
  children: React.ReactNode;
  v?: React.ReactNode;
}) {
  return (
    <span className="lgd">
      <i style={{ background: c }} />
      {children}
      {v != null ? <b>{v}</b> : null}
    </span>
  );
}

/** Barra de dos (o más) tramos sobre una pista, para repartos web/app. */
export function SBar({
  segs,
  max,
  title,
}: {
  segs: Array<{ v: number; c: string }>;
  /** Denominador común: sin esto cada barra se normaliza sola y no se comparan. */
  max?: number;
  title?: string;
}) {
  const tot = (max ?? segs.reduce((a, s) => a + s.v, 0)) || 1;
  return (
    <span className="sbar" title={title}>
      {segs.map((s, i) => (
        <i key={i} style={{ width: `${Math.max(0, (s.v / tot) * 100).toFixed(2)}%`, background: s.c }} />
      ))}
    </span>
  );
}

/** Grupo de chips excluyentes (rango, lente, filtro). */
export function Chips<T extends string | number>({
  value,
  options,
  onChange,
  style,
  className,
}: {
  value: T;
  options: Array<{ v: T; l: React.ReactNode; c?: React.ReactNode }>;
  onChange: (v: T) => void;
  style?: React.CSSProperties;
  className?: string;
}) {
  return (
    <span className={`chips${className ? ' ' + className : ''}`} style={style}>
      {options.map((o) => (
        <button
          key={String(o.v)}
          className={o.v === value ? 'on' : undefined}
          onClick={() => onChange(o.v)}
          type="button"
        >
          {o.l}
          {o.c != null ? <span className="c">{o.c}</span> : null}
        </button>
      ))}
    </span>
  );
}

/** Pestañas de sección (las del subrayado que se desliza). */
export function Tabs<T extends string>({
  value,
  options,
  onChange,
  right,
  id,
}: {
  value: T;
  options: Array<{ v: T; l: React.ReactNode; c?: React.ReactNode }>;
  onChange: (v: T) => void;
  right?: React.ReactNode;
  id?: string;
}) {
  return (
    <div className="tabs pega" id={id} style={{ paddingBottom: 0 }}>
      <span style={{ display: 'flex', alignItems: 'flex-end', gap: 16 }}>
        {options.map((o) => (
          <button
            key={o.v}
            className={o.v === value ? 'on' : undefined}
            onClick={() => onChange(o.v)}
            type="button"
          >
            {o.l}
            {o.c != null ? <span className="c">{o.c}</span> : null}
          </button>
        ))}
      </span>
      {right ? (
        <span style={{ marginLeft: 'auto', paddingBottom: 8 }}>{right}</span>
      ) : null}
      <span className="fade" />
    </div>
  );
}

export function Pill({
  children,
  kind = 'gh',
  sm,
  onClick,
  disabled,
  style,
  title,
  type = 'button',
}: {
  children: React.ReactNode;
  kind?: 'gh' | 'fill' | 'q' | 'ok' | 'dgr';
  sm?: boolean;
  onClick?: () => void;
  disabled?: boolean;
  style?: React.CSSProperties;
  title?: string;
  type?: 'button' | 'submit';
}) {
  return (
    <button
      type={type}
      className={`pill ${kind}${sm ? ' sm' : ''}`}
      onClick={onClick}
      disabled={disabled}
      style={style}
      title={title}
    >
      {children}
    </button>
  );
}

export function Tag({
  children,
  kind = 'nt',
  dot = true,
  style,
  title,
}: {
  children: React.ReactNode;
  kind?: 'ok' | 'pd' | 'er' | 'nt' | 'cp' | 'mb';
  dot?: boolean;
  style?: React.CSSProperties;
  title?: string;
}) {
  return (
    <span className={`tag ${kind}`} style={style} title={title}>
      {dot ? <i /> : null}
      {children}
    </span>
  );
}

export const Empty = ({ children }: { children: React.ReactNode }) => (
  <div className="empty">{children}</div>
);

export function Note({
  children,
  kind,
  className,
  style,
}: {
  children: React.ReactNode;
  kind?: 'warn' | 'dgr';
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <p className={`note${kind ? ' ' + kind : ''}${className ? ' ' + className : ''}`} style={style}>
      {children}
    </p>
  );
}

/** Buscador con la lupa adentro. */
export function Srch({
  value,
  onChange,
  placeholder,
  style,
  inputStyle,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  style?: React.CSSProperties;
  inputStyle?: React.CSSProperties;
}) {
  return (
    <span className="srch" style={style}>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6}>
        <circle cx="11" cy="11" r="7" />
        <path d="m20 20-3.5-3.5" />
      </svg>
      <input
        className="inp"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        style={inputStyle}
      />
    </span>
  );
}

/** Campo de formulario: rótulo, control y pista. */
export function Fld({
  label,
  children,
  hint,
  style,
}: {
  label?: React.ReactNode;
  children: React.ReactNode;
  hint?: React.ReactNode;
  style?: React.CSSProperties;
}) {
  return (
    <div className="fld" style={style}>
      {label ? <label>{label}</label> : null}
      {children}
      {hint ? <div className="hint">{hint}</div> : null}
    </div>
  );
}

/** Palanca del prototipo (no es un Switch de MUI: es la del diseño). */
export function Sw({
  on,
  onClick,
  label,
  disabled,
}: {
  on: boolean;
  onClick: () => void;
  label?: React.ReactNode;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      className="swrow"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      style={{ background: 'none', border: 0, padding: 0 }}
    >
      {label ? (
        <span className="sml" style={{ color: on ? 'var(--green)' : 'var(--faint)' }}>
          {label}
        </span>
      ) : null}
      <span className={`sw${on ? ' on' : ''}`} />
    </button>
  );
}

// ── Barra de composición ─────────────────────────────────────────────────────
export type Seg = {
  key: string;
  value: number;
  color: string;
  tip?: React.ReactNode;
};

export function CompBar({
  segs,
  hot,
  onHot,
  className,
}: {
  segs: Seg[];
  hot?: string | null;
  onHot?: (k: string | null) => void;
  className?: string;
}) {
  const ov = useOverlay();
  return (
    <div className={`compbar${className ? ' ' + className : ''}`}>
      {segs.map((s) => (
        <i
          key={s.key}
          className={hot ? (hot === s.key ? 'hot' : 'dim') : undefined}
          style={{ flex: Math.max(0.0001, s.value), background: s.color }}
          onMouseEnter={(e) => {
            onHot?.(s.key);
            if (s.tip) ov.tip(e, s.tip);
          }}
          onMouseMove={(e) => {
            if (s.tip) ov.tip(e, s.tip);
          }}
          onMouseLeave={() => {
            onHot?.(null);
            ov.tipOff();
          }}
        />
      ))}
    </div>
  );
}

// ── Lista de barras horizontales («de qué activo salieron», «por par»…) ──────
export type WfRow = {
  label: React.ReactNode;
  sub?: React.ReactNode;
  value: number;
  color?: string;
  title?: string;
};

export function Wf({
  rows,
  fmt,
  empty = 'Sin datos.',
}: {
  rows: WfRow[];
  fmt: (v: number) => string;
  empty?: React.ReactNode;
}) {
  const [hot, setHot] = React.useState(-1);
  if (!rows.length) return <Empty>{empty}</Empty>;
  const max = Math.max(1e-9, ...rows.map((r) => Math.abs(r.value)));
  return (
    <div className="wf">
      {rows.map((r, i) => (
        <div
          key={i}
          className={`wfr${hot >= 0 && hot !== i ? ' dim' : ''}`}
          onMouseEnter={() => setHot(i)}
          onMouseLeave={() => setHot(-1)}
        >
          <span className="k" title={r.title}>
            {r.label}
            {r.sub ? (
              <span
                style={{
                  display: 'block',
                  fontSize: '.62rem',
                  color: 'var(--faint)',
                  marginTop: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {r.sub}
              </span>
            ) : null}
          </span>
          <span className="tr">
            <span
              className="bar"
              style={{
                width: `${((Math.abs(r.value) / max) * 100).toFixed(1)}%`,
                background: r.color || cc(i),
                opacity: 0.55,
              }}
            />
          </span>
          <span className="v">{fmt(r.value)}</span>
        </div>
      ))}
    </div>
  );
}

// ── Filas ────────────────────────────────────────────────────────────────────
export function Rows({
  children,
  className,
  style,
}: {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <div className={`rows in${className ? ' ' + className : ''}`} style={style}>
      {children}
    </div>
  );
}

export function RHead({
  cols,
  children,
  ord,
}: {
  cols: string;
  children: React.ReactNode;
  ord?: boolean;
}) {
  return (
    <div className={`rhead${ord ? ' ord' : ''}`} style={{ gridTemplateColumns: cols }}>
      {children}
    </div>
  );
}

export function Row({
  cols,
  children,
  onClick,
  open,
  fx = true,
  style,
  title,
}: {
  cols: string;
  children: React.ReactNode;
  onClick?: () => void;
  open?: boolean;
  fx?: boolean;
  style?: React.CSSProperties;
  title?: string;
}) {
  return (
    <div
      className={`row${fx ? ' fx' : ''}${onClick ? ' clk' : ''}${open ? ' open' : ''}`}
      style={{ gridTemplateColumns: cols, ...style }}
      onClick={onClick}
      title={title}
      role={onClick ? 'button' : undefined}
    >
      {children}
    </div>
  );
}

/** Eje horizontal bajo un gráfico: primera etiqueta, del medio y «ahora». */
export function Xax({ labels }: { labels: React.ReactNode[] }) {
  return (
    <div className="xax">
      {labels.map((l, i) => (
        <span key={i} className={i === labels.length - 1 ? 'now' : undefined}>
          {l}
        </span>
      ))}
    </div>
  );
}

/** Cargando: el mismo hueco que ocupará el contenido, sin saltos. */
export function Cargando({ alto = 120 }: { alto?: number }) {
  return (
    <div
      className="sml"
      style={{
        display: 'grid',
        placeItems: 'center',
        height: alto,
        color: 'var(--faint)',
      }}
    >
      Cargando…
    </div>
  );
}
